/* eslint-disable */
/* eslint-disable react/no-unstable-nested-components */

import React, { useEffect, useState } from 'react';
import { FullScreenLoading, LargeButton, PassInput, RouterLink } from 'pg-ui-library';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import logoSvg from 'src/assets/logo_pride.svg';
import background from 'src/assets/signUpBg.png';

import getSecurityQuestionsService from 'src/services/get-security-questions.service';
import SignupService from 'src/services/signup.service';

import {
  BackgroundImage,
  Container,
  FormContainer,
  MenuContainer,
  FormTitle,
  ImageContainer,
  Logo,
  SignUpInput,
  FooterContainer,
  FooterText,
  LinkContainer,
  Text,
  RedText,
  Input,
  InputSelect,
} from './styles';

import { ISecurityQuestion } from 'src/interfaces';

/* ---------------- Validation ---------------- */

const validationSchema = yup.object({
  password: yup.string().required('Password is required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Confirm password is required'),
  questionId: yup.string().required('Security question is required'),
  answer: yup.string().required('Answer is required'),
});

/* ---------------- Component ---------------- */

const SetClientPassword: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [questions, setQuestions] = useState<{ label: string; value: string }[]>([]);

  const [isTokenValid, setIsTokenValid] = useState(true);

  const [userInfo, setUserInfo] = useState<{
    firstName: string;
    middleName?: string;
    lastName: string;
    email: string;
    username: string;
  } | null>(null);

  useEffect(() => {
    if (!token) return;

    const loadUserInfo = async () => {
      try {
        const data = await SignupService.passwordSetupLookup(token);
        setUserInfo(data);
        setIsTokenValid(true);
      } catch {
        setIsTokenValid(false);
        setError('Invalid or expired password setup link');
      }
    };

    loadUserInfo();
  }, [token]);

  /* -------- Load Security Questions -------- */

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const data: ISecurityQuestion[] = await getSecurityQuestionsService.getQuestions();

        setQuestions(
          data.map(q => ({
            label: q.question,
            value: q.id,
          }))
        );
      } catch {
        setError('Failed to load security questions');
      }
    };

    loadQuestions();
  }, []);

  /* -------- Invalid Token -------- */

  if (!token) {
    return (
      <Container>
        <MenuContainer>
          <FormContainer>
            <Logo src={logoSvg} />
            <RedText>Invalid or expired password setup link.</RedText>
            <RouterLink to="/login">Go to Login</RouterLink>
          </FormContainer>
        </MenuContainer>
      </Container>
    );
  }

  /* -------- Render -------- */

  return (
    <>
      {loading && <FullScreenLoading />}

      <Container>
        <MenuContainer>
          <FormContainer>
            <Logo src={logoSvg} />
            {/* <FormTitle>Hi {userInfo?.firstName}, please set your password</FormTitle>
            <Text>Email-{userInfo?.email}</Text> */}

            {isTokenValid && userInfo ? (
              <>
                <FormTitle>Hi {userInfo.firstName}, please set your password</FormTitle>
                <Text>Email - {userInfo.email}</Text>
              </>
            ) : (
              <>
                <FormTitle>Password Setup</FormTitle>
                <RedText>{error}</RedText>
              </>
            )}

            <Formik
              initialValues={{
                password: '',
                confirmPassword: '',
                questionId: '',
                answer: '',
              }}
              validationSchema={validationSchema}
              onSubmit={async values => {
                try {
                  setLoading(true);
                  setError('');

                  await SignupService.setPassword(token, {
                    password: values.password,
                    confirmPassword: values.confirmPassword,
                    securityQuestion: {
                      questionId: values.questionId,
                      answer: values.answer,
                    },
                  });

                  navigate('/');
                } catch (err: any) {
                  setError(err?.response?.data?.message || 'Failed to set password. Please try again.');
                } finally {
                  setLoading(false);
                }
              }}
            >
              {({ handleChange, handleSubmit, setFieldValue, values, errors, touched }) => (
                <Form onSubmit={handleSubmit}>
                  <SignUpInput>
                    <PassInput name="password" placeholder="Password" handleChange={handleChange} />
                    {touched.password && errors.password && <RedText>{errors.password}</RedText>}
                  </SignUpInput>

                  <SignUpInput>
                    <PassInput name="confirmPassword" placeholder="Confirm Password" handleChange={handleChange} />
                    {touched.confirmPassword && errors.confirmPassword && <RedText>{errors.confirmPassword}</RedText>}
                  </SignUpInput>

                  <SignUpInput>
                    <InputSelect
                      name="questionId"
                      value={values.questionId}
                      onChange={e => setFieldValue('questionId', e.target.value)}
                    >
                      <option value="" disabled>
                        Select Security Question
                      </option>

                      {questions.map(q => (
                        <option key={q.value} value={q.value}>
                          {q.label}
                        </option>
                      ))}
                    </InputSelect>
                    {touched.questionId && errors.questionId && <RedText>{errors.questionId}</RedText>}
                  </SignUpInput>

                  <SignUpInput>
                    <Input name="answer" placeholder="Your Answer" onChange={handleChange} />
                    {touched.answer && errors.answer && <RedText>{errors.answer}</RedText>}
                  </SignUpInput>

                  {error && <RedText>{error}</RedText>}

                  {/* <LargeButton type="submit">Set Password</LargeButton> */}
                  <LargeButton type="submit" disabled={!isTokenValid}>
                    Set Password
                  </LargeButton>
                </Form>
              )}
            </Formik>

            {/* <Text>Already have an account?</Text>
            <RouterLink to="/login">Login</RouterLink> */}
          </FormContainer>

          <FooterContainer>
            <FooterText>{`PRIDE GLOBAL ${currentYear} ALL RIGHTS RESERVED`}</FooterText>
            <LinkContainer>
              <RouterLink to="https://prideglobal.com/terms-of-use/" target="_blank">
                {t('signUp.footer.termsOfUse')}
              </RouterLink>
              <RouterLink to="https://prideglobal.com/privacy-policy/" target="_blank">
                {t('signUp.footer.privacyPolicy')}
              </RouterLink>
              <RouterLink to="mailto:employeesupport@prideglobal.com" target="_blank">
                {t('signUp.footer.support')}
              </RouterLink>
            </LinkContainer>
          </FooterContainer>
        </MenuContainer>

        <ImageContainer>
          <BackgroundImage src={background} />
        </ImageContainer>
      </Container>
    </>
  );
};

export default SetClientPassword;
